import { useRef, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { UndoIcon } from 'foundations-assets/icons/components/UndoIcon';
import { Box } from 'foundations-components/transitional/components/Box';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { DO_NOT_DISPLAY_THREAD } from 'copilot-toolkit/constants/PromptThread';
import { INTERACTIVE_TOOL_BUTTON_CLICK } from 'copilot-toolkit/constants/Tools';
import { getCurrentStateOfFunctionValuesByName, getUndoAction, getAreAllParametersTheSame } from 'copilot-toolkit/utils/interactiveModeUtils';
import { safeParse } from 'copilot-toolkit/utils/jsonSchemaUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ActionRenderer = ({
  message,
  actionFn,
  state
}) => {
  var _agentPayload$jsonCon, _agentPayload;
  const agentPayload = safeParse((_agentPayload$jsonCon = message === null || message === void 0 || (_agentPayload = message.agentPayload) === null || _agentPayload === void 0 ? void 0 : _agentPayload.jsonContent) !== null && _agentPayload$jsonCon !== void 0 ? _agentPayload$jsonCon : '');
  const actionsToRender = agentPayload === null || agentPayload === void 0 ? void 0 : agentPayload.feInteractionsToExecute;

  // Store previous values for undo
  const prevValuesRef = useRef({});

  // Track execution state
  const [executedActions, setExecutedActions] = useState({});
  if (!actionsToRender) {
    return null;
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: actionsToRender.map((action, idx) => {
      var _customFEActions;
      const {
        functionName = '',
        buttonExplanation = '',
        buttonText = '',
        showAsButton = false,
        buttonHeader = ''
      } = action || {};
      const uniqueKey = `${functionName}-${idx}-${buttonHeader}`;
      const currentValuesOfFunction = getCurrentStateOfFunctionValuesByName(state === null || state === void 0 || (_customFEActions = state.customFEActions) === null || _customFEActions === void 0 ? void 0 : _customFEActions.interactions, functionName);
      if (!currentValuesOfFunction) {
        return null;
      }

      // Store initial values for undo
      if (!prevValuesRef.current[uniqueKey]) {
        prevValuesRef.current[uniqueKey] = currentValuesOfFunction;
      }
      const areAllParametersTheSame = getAreAllParametersTheSame(prevValuesRef.current[uniqueKey], action);
      const prevValues = prevValuesRef.current[uniqueKey];
      const undoActionToInvoke = getUndoAction(prevValues, action);
      const isExecuted = executedActions[uniqueKey] || false;
      const undoAction = () => {
        actionFn({
          actionType: INTERACTIVE_TOOL_BUTTON_CLICK,
          actionToInvoke: undoActionToInvoke,
          userThread: DO_NOT_DISPLAY_THREAD
        });
        setExecutedActions(prev => {
          const newState = Object.assign({}, prev, {
            [uniqueKey]: false
          });
          return newState;
        });
      };
      const executeAction = () => {
        actionFn({
          actionType: INTERACTIVE_TOOL_BUTTON_CLICK,
          actionToInvoke: action,
          userThread: DO_NOT_DISPLAY_THREAD
        });
        setExecutedActions(prev => {
          const newState = Object.assign({}, prev, {
            [uniqueKey]: true
          });
          return newState;
        });
      };

      // Logic: Initial render respects showAsButton, then toggles based on user interaction
      const hasUserInteracted = (uniqueKey in executedActions);
      let shouldShowExecuteButton;
      if (!hasUserInteracted) {
        // First render - respect showAsButton from LLM
        shouldShowExecuteButton = showAsButton;
      } else {
        // After user interaction - toggle based on execution state
        shouldShowExecuteButton = !isExecuted;
      }
      const actionToExecute = shouldShowExecuteButton ? executeAction : undoAction;
      const buttonTextToDisplay = shouldShowExecuteButton ? buttonText : unescapedText('copilotPlugins.interactiveModePlugin.undo');
      return /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        alignItems: "start",
        paddingBlockEnd: "200",
        children: [/*#__PURE__*/_jsx(Box, {
          children: /*#__PURE__*/_jsxs(List, {
            styled: true,
            paddingInlineStart: "500",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(ListItem, {
                children: buttonHeader
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              children: buttonExplanation
            })]
          })
        }), /*#__PURE__*/_jsx(Box, {
          grow: 0,
          maxInlineSize: "250px",
          paddingInlineStart: "500",
          children: /*#__PURE__*/_jsxs(Button, {
            borderWidth: "100",
            borderRadius: "full",
            truncate: true,
            onClick: actionToExecute,
            disabled: areAllParametersTheSame,
            size: "xs",
            use: "tertiary-extra-light",
            children: [!shouldShowExecuteButton && /*#__PURE__*/_jsx(UndoIcon, {}), buttonTextToDisplay]
          }, uniqueKey)
        })]
      }, uniqueKey);
    })
  });
};

// We may eventually style these responses uniquely, but for now using standard copilot responses.
export const InteractiveModePlugin = {
  gate: 'Copilot:Collaborate',
  intentTypes: ['interactive_mode'],
  collaborateCopilotMessageTypes: [],
  // Default rendering for now.
  sectionRenderer: null,
  actionsRenderer: ActionRenderer
};